import { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { DailyNavSecondaryChangeFromMark } from 'api';
import { formatPrice, formatSignedPercent, formatThreshold, toNumber } from './formatters';

interface ChangeFromMarkPanelProps {
  changeFromMark: DailyNavSecondaryChangeFromMark;
}

// The server's own reason, phrased for a reviewer. fx_unavailable is NOT missing
// data -- both numbers exist and the ratio was withheld on purpose -- so it reads
// differently from an absent price, which is the whole point of a reason field.
const CHANGE_FROM_MARK_REASONS: Record<string, string> = {
  no_mark: 'No prior final valuation carried a price per share, so there is no mark to compare against.',
  zero_mark: 'The mark is zero, so there is no meaningful percentage away from it.',
  no_price: 'No provider reported a price, so there is nothing to compare to the mark.',
  mark_currency_unknown:
    'Nothing records which currency this mark is in, so it cannot be compared to a price rather than being assumed.',
  fx_unavailable:
    'No exchange rate was available to restate the mark in the reporting currency, so no percentage is shown rather than one computed across two.',
  price_currency_mismatch:
    'The providers did not report in the firm\u2019s currency. Converting their prices is not done here, so no percentage is shown rather than one computed across two.',
};

// Deliberately outside the provider table: this is not a per-provider figure and
// not a window, so a row in that table would sit under headings that do not
// apply to it. Spells out the whole division, because the reader's question here
// is "how far from our mark, and against what number" -- and the two sides come
// from different systems, in currencies that need not match.
const ChangeFromMarkPanel: FC<ChangeFromMarkPanelProps> = ({ changeFromMark }) => {
  const hasComparison = toNumber(changeFromMark.pct) != null;
  return (
    <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }} id="secondary-breakdown-change-from-mark">
      <Typography variant="body2" fontWeight={600}>
        Change from mark
      </Typography>
      {hasComparison ? (
        <>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {/* Both sides in the reporting currency, which is what makes the
                division meaningful. */}
            Average {formatPrice(changeFromMark.average_price, changeFromMark.reporting_currency)} against a mark of{' '}
            {formatPrice(changeFromMark.mark_reporting, changeFromMark.reporting_currency)} ={' '}
            <Typography
              component="span"
              variant="body2"
              fontWeight={600}
              sx={{ color: changeFromMark.exceeded ? 'error.main' : 'inherit' }}>
              {formatSignedPercent(changeFromMark.pct)}
            </Typography>
          </Typography>
          {/* Only when a conversion actually happened: showing "USD 12.00
              converted from USD 12.00" is noise, but hiding a real FX step
              would leave the reader unable to tell where the number came from.
              The null check matters -- an unknown source currency is treated as
              already being the reporting one, so no conversion occurred and
              claiming otherwise would print an unlabelled figure. */}
          {changeFromMark.mark_currency &&
            changeFromMark.mark_currency !== changeFromMark.reporting_currency && (
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              Mark converted from {formatPrice(changeFromMark.mark, changeFromMark.mark_currency)} at the report
                date&rsquo;s rate.
            </Typography>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.5, color: 'text.secondary' }}>
          {(changeFromMark.unavailable_reason &&
            CHANGE_FROM_MARK_REASONS[changeFromMark.unavailable_reason]) ??
            'No percentage is available for this row.'}
        </Typography>
      )}
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        Mark is the price per share from the last final valuation, converted into{' '}
        {changeFromMark.reporting_currency}. Threshold {formatThreshold(changeFromMark)}
        {changeFromMark.exceeded ? ' — exceeded' : ''}
      </Typography>
    </Box>
  );
};

export default ChangeFromMarkPanel;
